import Image from 'next/image'
import {FC} from 'react'

import {useWhyTramaContent} from '@/content/marketing-pages/whyTrama'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {Jurisdiction} from '@/utils/countries/constants'

import {ComparisonTable} from '../../comparison-table/ComparisonTable'
import {ListItemWithCheckmark} from '../../content/ListItemWithCheckmark'
import {Text, TextFontWeight, TextVariant} from '../../content/Text'
import {CheckCircleIcon} from '../../icons/CheckCircleIcon'
import {CloseCircleIcon} from '../../icons/CloseCircleIcon'
import {TramaLogo} from '../../icons/TramaLogo'
import {WhyTramaComparisonBenefitTrans} from './WhyTramaComparisonBenefitTrans'

type WhyTramaComparisonTableDesktopProps = {
  numOfRegisteredTrademarks: number
  country?: Jurisdiction
}

export const WhyTramaComparisonTableDesktop: FC<
  React.PropsWithChildren<WhyTramaComparisonTableDesktopProps>
> = ({numOfRegisteredTrademarks, country}) => {
  const {t} = useTranslation()
  const {industryAverageTitle, benefitColumnTitle, benefits} = useWhyTramaContent(country)

  return (
    <>
      <ComparisonTable
        header={
          <tr>
            <th scope="col">
              <span className="visually-hidden">{t(benefitColumnTitle)}</span>
            </th>
            <th scope="col">
              <Text variant={TextVariant.h5} element="span">
                {t(industryAverageTitle)}
              </Text>
            </th>
            <th scope="col">
              <TramaLogo />
            </th>
          </tr>
        }
      >
        {benefits.map(({imageSrc, imageAlt, title, average, trama}) => (
          <tr key={title}>
            <td>
              <div className="wrapper">
                <div className="icon">
                  <Image
                    src={imageSrc}
                    alt={imageAlt}
                    width={24}
                    height={24}
                    style={{objectFit: 'contain'}}
                  />
                </div>
                <Text variant={TextVariant.h5} element="span" fontWeight={TextFontWeight.bold}>
                  {t(title)}
                </Text>
              </div>
            </td>
            <td>
              <div className="wrapper">
                {average ? (
                  <CloseCircleIcon
                    backgroundColor={theme.palette.error.main}
                    iconColor={theme.palette.text.white}
                  />
                ) : (
                  <div className="placeholder-icon"></div>
                )}
                <Text>{average ? t(average) : '—'}</Text>
              </div>
            </td>
            <td>
              <ListItemWithCheckmark
                icon={<CheckCircleIcon backgroundColor={theme.palette.success.main} />}
                text={
                  <WhyTramaComparisonBenefitTrans
                    description={trama.description}
                    numOfRegisteredTrademarks={numOfRegisteredTrademarks}
                    linkHrefs={trama.linkHrefs}
                    isLocalizedLink={trama.isLocalizedLink}
                  />
                }
                gap={theme.spacing[2.5]}
                verticalAlign="middle"
                element="div"
              />
            </td>
          </tr>
        ))}
      </ComparisonTable>

      <style jsx>{`
        .wrapper {
          display: flex;
          align-items: center;
          gap: ${theme.spacing[2.5]};
        }
        .placeholder-icon {
          width: 20px;
          height: 20px;
        }
        .icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          background-color: ${theme.palette.butter[200]};
          border-radius: 12px;
        }
      `}</style>
    </>
  )
}
